/*
  Kilvon landing template: shared behaviour (contract: _local/kilvon/landing-template/CONTRACT.md, sections 7-8).
  Loaded once by kl-hero-buy (defer). Plain ES2018, no dependencies. Every hook is optional:
  a page without a given element simply skips that part.

  Hooks: data-kl-offer, data-kl-pack, data-kl-addon, data-kl-atc, data-kl-scroll, data-kl-bind,
         data-kl-sticky, data-kl-sticky-hide, data-kl-reveal, data-kl-money-format; plus the hero
         gallery (data-kl-gallery / data-kl-slides / data-kl-thumb / data-kl-count).
  Events: kl:pack-change, kl:cart-added, kl:cart-error on document.
  API:    window.KL.state, window.KL.addToCart(), window.KL.selectPack(index).
*/
(function () {
  'use strict';
  if (window.KL && window.KL.__loaded) return;

  // ---------- pure helpers (also exported on window.KL for tests) ----------

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands === undefined ? ',' : thousands;
    decimal = decimal === undefined ? '.' : decimal;
    if (isNaN(cents) || cents === null) return '0';
    var value = (cents / 100).toFixed(precision);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  // Shopify money format, e.g. "${{amount}}" or "{{amount_with_comma_separator}} zł". Returns plain text.
  function formatMoney(cents, format) {
    cents = Math.round(Number(cents) || 0);
    var fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    var re = /\{\{\s*(\w+)\s*\}\}/;
    var m = fmt.match(re);
    if (!m) return fmt;
    var out;
    switch (m[1]) {
      case 'amount': out = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': out = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': out = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': out = formatWithDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': out = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': out = formatWithDelimiters(cents, 2, ' ', '.'); break;
      default: out = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(re, out).replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ');
  }

  // Contract section 8: price = unit × qty × (1 − discount%), rounded to the cent;
  // compareAt = max(unit compare-at, unit price) × qty. Same order of operations as the Liquid.
  function packPrice(unitPrice, unitCompare, quantity, discountPercent) {
    var q = Math.max(1, parseInt(quantity, 10) || 1);
    var d = Math.min(100, Math.max(0, Number(discountPercent) || 0));
    var p = Math.round(Number(unitPrice) || 0);
    var c = Math.max(Math.round(Number(unitCompare) || 0), p);
    var price = Math.round((p * q * (100 - d)) / 100);
    var compareAt = c * q;
    var savings = Math.max(0, compareAt - price);
    return {
      quantity: q,
      price: price,
      compareAt: compareAt,
      savings: savings,
      savingsPercent: compareAt > 0 ? Math.floor((savings * 100) / compareAt) : 0, // never rounded up
      unitPrice: Math.round(price / q),
      unitCompareAt: c
    };
  }

  // The /cart/add.js body: main variant × pack quantity, then each checked add-on × 1.
  function buildAddPayload(variantId, quantity, addons, sections) {
    var items = [{ id: Number(variantId), quantity: Math.max(1, parseInt(quantity, 10) || 1) }];
    (addons || []).forEach(function (a) {
      if (a && a.checked && a.variantId) items.push({ id: Number(a.variantId), quantity: 1 });
    });
    var body = { items: items };
    if (sections && sections.length) {
      body.sections = sections.join(',');
      if (typeof location !== 'undefined') body.sections_url = location.pathname;
    }
    return body;
  }

  var KL = (window.KL = window.KL || {});
  KL.__loaded = true;
  KL.formatMoney = formatMoney;
  KL.packPrice = packPrice;
  KL.buildAddPayload = buildAddPayload;
  KL.state = KL.state || null;

  if (typeof document === 'undefined') return;

  var isStatic = /[?&]static(=|&|$)/.test(location.search) || /static/.test(location.hash);
  var offer = null;

  function qa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function fire(name, detail) {
    var ev;
    try { ev = new CustomEvent(name, { detail: detail }); }
    catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent(name, false, false, detail); }
    document.dispatchEvent(ev);
  }
  function moneyFormat() {
    return (offer && offer.getAttribute('data-kl-money-format')) || '${{amount}}';
  }
  function packs() { return offer ? qa('[data-kl-pack]', offer) : []; }
  function addonInputs() { return offer ? qa('[data-kl-addon]', offer) : []; }
  function selectedPack() {
    var list = packs();
    for (var i = 0; i < list.length; i++) if (list[i].classList.contains('is-selected')) return list[i];
    return list[0] || null;
  }

  // ---------- state + binds ----------

  function computeState() {
    if (!offer) return null;
    var sel = selectedPack();
    var qty = sel ? parseInt(sel.getAttribute('data-kl-qty'), 10) || 1 : 1;
    var disc = sel ? Number(sel.getAttribute('data-kl-discount')) || 0 : 0;
    var p = packPrice(offer.getAttribute('data-kl-price'), offer.getAttribute('data-kl-compare-at'), qty, disc);
    var fmt = moneyFormat();
    // ticked add-ons: their price and compare-at join the totals the sticky bar shows
    var addOnPrice = 0, addOnCompare = 0;
    addonInputs().forEach(function (el) {
      if (!el.checked) return;
      var ap = Math.round(Number(el.getAttribute('data-kl-price')) || 0);
      addOnPrice += ap;
      addOnCompare += Math.max(ap, Math.round(Number(el.getAttribute('data-kl-compare-at')) || 0));
    });
    var total = p.price + addOnPrice;
    var totalCompare = p.compareAt + addOnCompare;
    return {
      index: sel ? parseInt(sel.getAttribute('data-kl-pack'), 10) || 0 : 0,
      quantity: p.quantity,
      label: sel ? sel.getAttribute('data-kl-label') || '' : '',
      discountPercent: disc,
      variantId: Number(offer.getAttribute('data-kl-variant-id')) || null,
      available: offer.getAttribute('data-kl-available') === 'true',
      price: p.price,
      compareAt: p.compareAt,
      savings: p.savings,
      savingsPercent: p.savingsPercent,
      priceFormatted: formatMoney(p.price, fmt),
      compareAtFormatted: formatMoney(p.compareAt, fmt),
      savingsFormatted: formatMoney(p.savings, fmt),
      unitPriceFormatted: formatMoney(p.unitPrice, fmt),
      unitCompareFormatted: formatMoney(p.unitCompareAt, fmt),
      addonsPrice: addOnPrice,
      totalPrice: total,
      totalCompareAt: totalCompare,
      totalSavingsPercent: totalCompare > 0 ? Math.floor(((totalCompare - total) * 100) / totalCompare) : 0, // never rounded up
      totalPriceFormatted: formatMoney(total, fmt),
      totalCompareAtFormatted: formatMoney(totalCompare, fmt),
      addons: addonInputs().map(function (el) {
        return { variantId: Number(el.getAttribute('data-kl-variant-id')) || null, checked: !!el.checked };
      })
    };
  }

  function applyBinds(s) {
    qa('[data-kl-bind]').forEach(function (el) {
      var k = el.getAttribute('data-kl-bind');
      var t = null;
      if (k === 'label') t = s.label;
      else if (k === 'price') t = s.priceFormatted;
      else if (k === 'compare') t = s.savings > 0 ? s.compareAtFormatted : '';
      else if (k === 'save') t = s.savingsFormatted;
      else if (k === 'qty') t = String(s.quantity);
      else if (k === 'unit') t = s.unitPriceFormatted;
      else if (k === 'total') t = s.totalPriceFormatted;
      else if (k === 'total-compare') t = s.totalCompareAt > s.totalPrice ? s.totalCompareAtFormatted : '';
      else if (k === 'unit-compare') { t = s.unitCompareFormatted; el.hidden = !(s.savings > 0); }
      if (t !== null) el.textContent = t;
    });
    qa('[data-kl-save-badge]').forEach(function (el) {
      var tpl = el.getAttribute('data-kl-save-template') || '';
      el.textContent = tpl.replace('[percent]', String(s.savingsPercent));
      el.hidden = !(s.savingsPercent > 0);
    });
    qa('[data-kl-atc]').forEach(function (b) {
      if (!s.available) { b.disabled = true; b.setAttribute('aria-disabled', 'true'); }
    });
    var qtyInput = offer && offer.querySelector('[data-kl-qty-input]');
    if (qtyInput) qtyInput.value = s.quantity;
  }

  function update() {
    var s = computeState();
    if (!s) return;
    KL.state = s;
    applyBinds(s);
    fire('kl:pack-change', s);
  }

  KL.selectPack = function (index) {
    var list = packs();
    if (!list.length) return;
    var i = Math.max(0, Math.min(list.length - 1, parseInt(index, 10) || 0));
    list.forEach(function (p, k) {
      var on = k === i;
      p.classList.toggle('is-selected', on);
      p.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    update();
  };

  // ---------- add to cart ----------

  function setBusy(on) {
    qa('[data-kl-atc]').forEach(function (b) {
      if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
    });
  }
  function showError(msg) {
    var el = offer && offer.querySelector('[data-kl-atc-error]');
    if (el) { el.textContent = msg; el.hidden = !msg; }
  }
  function cartRoot() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  var adding = false;
  KL.addToCart = function () {
    var s = KL.state || computeState();
    if (!s || !s.variantId) return Promise.resolve(null);
    if (!s.available || adding) return Promise.resolve(null);
    adding = true;
    setBusy(true);
    showError('');

    var drawer = document.querySelector('cart-drawer');
    var canDrawer = !!(drawer && window.customElements && customElements.get('cart-drawer') &&
      typeof drawer.renderContents === 'function');
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    if (canDrawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        var ids = drawer.getSectionsToRender().map(function (x) { return x.section || x.id; }).filter(Boolean);
        if (ids.length) sections = ids.filter(function (v, i, a) { return a.indexOf(v) === i; });
      } catch (e) { /* keep defaults */ }
    }
    var body = buildAddPayload(s.variantId, s.quantity, s.addons, canDrawer ? sections : null);

    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(function (res) {
        var j = res.json || {};
        if (!res.ok || j.status) {
          var msg = j.description || j.message || 'Could not add to cart';
          showError(msg);
          fire('kl:cart-error', { message: msg });
          return null;
        }
        if (canDrawer) {
          if (j.items && j.items[0] && j.id === undefined) j.id = j.items[0].id;
          drawer.classList.remove('is-empty');
          try {
            drawer.renderContents(j);
          } catch (e) {
            fire('kl:cart-added', { items: body.items, response: j });
            location.href = cartRoot() + 'cart';
            return j;
          }
          fire('kl:cart-added', { items: body.items, response: j });
        } else {
          fire('kl:cart-added', { items: body.items, response: j });
          location.href = cartRoot() + 'cart';
        }
        return j;
      })
      .catch(function (err) {
        var msg = (err && err.message) || 'Could not add to cart';
        showError(msg);
        fire('kl:cart-error', { message: msg });
        return null;
      })
      .then(function (out) { adding = false; setBusy(false); return out; });
  };

  // ---------- events (delegated, so later or absent elements are fine) ----------

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var pack = t.closest('[data-kl-pack]');
    // a toggle or link inside a pack card (the free-kit panel) works as itself and never re-selects the pack
    if (pack && t.closest('[data-kl-pack-ignore]')) pack = null;
    if (pack && offer && offer.contains(pack)) {
      e.preventDefault();
      KL.selectPack(packs().indexOf(pack));
      return;
    }
    var atc = t.closest('[data-kl-atc]');
    if (atc) {
      e.preventDefault();
      if (!atc.disabled) KL.addToCart();
      return;
    }
    // cart icon: open the theme's cart drawer, the same one add to cart opens; /cart stays the no-JS fallback
    var co = t.closest('[data-kl-cart-open]');
    if (co) {
      var dr = document.querySelector('cart-drawer');
      if (dr && typeof dr.open === 'function') {
        e.preventDefault();
        try { dr.open(co); } catch (err) { location.href = co.getAttribute('href') || cartRoot() + 'cart'; }
      }
      return;
    }
    var faq = t.closest('a[href="#faq"], a[href="#reviews"]');
    if (faq) {
      var fq = document.querySelector('[data-kl-section="' + faq.getAttribute('href').slice(1) + '"]');
      if (fq) {
        e.preventDefault();
        fq.scrollIntoView({ behavior: isStatic ? 'auto' : 'smooth', block: 'start' });
      }
      return;
    }
    var sc = t.closest('[data-kl-scroll]');
    if (sc && sc.getAttribute('data-kl-scroll') === 'offer') {
      var target = document.querySelector('[data-kl-offer]');
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: isStatic ? 'auto' : 'smooth', block: 'start' });
      }
    }
  });

  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-kl-addon]')) update();
  });

  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && offer && offer.contains(f)) { e.preventDefault(); KL.addToCart(); }
  });

  // ---------- gallery ----------

  // Always opens on slide 1. The browser can hand the page back with the slide row still
  // scrolled (back/forward cache, or its own scroll restore on reload), which showed slide 8 of 8
  // on open; init and every pageshow put it back to the first slide before syncing.
  function initGallery(root) {
    var track = root.querySelector('[data-kl-slides]');
    if (!track) return;
    var thumbs = qa('[data-kl-thumb]', root);
    var count = root.querySelector('[data-kl-count]');
    var prev = root.querySelector('[data-kl-prev]');
    var next = root.querySelector('[data-kl-next]');
    var total = track.children.length;
    function idx() { return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0; }
    function sync() {
      var i = Math.max(0, Math.min(total - 1, idx()));
      thumbs.forEach(function (t, k) { t.classList.toggle('is-active', k === i); t.setAttribute('aria-current', k === i ? 'true' : 'false'); });
      if (count) count.textContent = (i + 1) + ' / ' + total;
      var th = thumbs[i];
      var row = th && th.parentNode;
      if (row && row.scrollWidth > row.clientWidth) {
        var left = th.offsetLeft - row.offsetLeft;
        if (left < row.scrollLeft) row.scrollLeft = left;
        else if (left + th.clientWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = left + th.clientWidth - row.clientWidth;
      }
    }
    function go(i, smooth) {
      i = Math.max(0, Math.min(total - 1, i));
      track.scrollTo({ left: i * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    }
    function reset() {
      var row = thumbs[0] && thumbs[0].parentNode;
      track.scrollLeft = 0;
      if (row) row.scrollLeft = 0;
      sync();
    }
    var timer = null;
    track.addEventListener('scroll', function () { clearTimeout(timer); timer = setTimeout(sync, 60); }, { passive: true });
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { go(parseInt(t.getAttribute('data-kl-thumb'), 10) || 0, true); });
    });
    if (prev) prev.addEventListener('click', function () { var i = idx(); go(i <= 0 ? total - 1 : i - 1, true); });
    if (next) next.addEventListener('click', function () { var i = idx(); go(i >= total - 1 ? 0 : i + 1, true); });
    reset();
    window.addEventListener('pageshow', reset);
    // a reload can restore the row's scroll after this script runs; catch it once on load
    window.addEventListener('load', function () { if (track.scrollLeft !== 0) reset(); }, { once: true });
  }

  // ---------- reveal ----------

  function initReveal() {
    var els = qa('[data-kl-reveal]');
    if (!els.length) return;
    if (isStatic || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-on'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-on'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -30% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  // ---------- sticky bar ----------

  function initSticky() {
    var bars = qa('[data-kl-sticky]');
    var target = document.querySelector('[data-kl-offer]');
    if (!bars.length) return;
    function set(show) {
      bars.forEach(function (b) {
        b.classList.toggle('is-shown', show);
        b.setAttribute('aria-hidden', show ? 'false' : 'true');
      });
    }
    set(false);
    if (!target || isStatic || !('IntersectionObserver' in window)) return;
    var offerAbove = false;
    var hidersInView = new Set();
    function apply() { set(offerAbove && hidersInView.size === 0); }
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        offerAbove = !en.isIntersecting && en.boundingClientRect.bottom < 0;
      });
      apply();
    }).observe(target);
    var hiders = qa('[data-kl-sticky-hide]');
    if (hiders.length) {
      var hio = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) hidersInView.add(en.target); else hidersInView.delete(en.target);
        });
        apply();
      });
      hiders.forEach(function (h) { hio.observe(h); });
    }
  }

  // ---------- init ----------

  function init() {
    if (isStatic) qa('.kl-page').forEach(function (p) { p.classList.add('kl-static'); });
    offer = document.querySelector('[data-kl-offer]');
    qa('[data-kl-gallery]').forEach(initGallery);
    initReveal();
    initSticky();
    if (offer) {
      var sel = selectedPack();
      if (sel) KL.selectPack(packs().indexOf(sel));
      else update();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
